import React from 'react';
import type { NodeProps } from 'reactflow';
import { usePersonaName } from '../../../../hooks/usePersonaColor';
import type { ClusterAabbData } from '../types';

// Trailmap L2 Drip 2 — a bounding box painted BEHIND a cluster's tiles (the
// layout `unshift`es it so RF paints it first). Header names the cluster value +
// count. Provenance is the label (lean AABB-only — no group→origin edge).
const ROUND_LABEL = (v: string | null) => (v == null ? 'No round' : `Round ${v}`);

const ClusterAabbNode: React.FC<NodeProps<ClusterAabbData>> = ({ data }) => {
  const { dim, value, count, bbox, soloOfDim } = data;
  // usePersonaName is a hook → must be called unconditionally; harmless for non-persona dims.
  const personaName = usePersonaName(value ?? undefined);

  // usePersonaName returns `Peer xxxxxxxx` for a present-but-unresolved agent_id
  // (no CouncilProvider / stale id) — treat that as unknown per spec §3.9.
  const resolvedName = personaName && !personaName.startsWith('Peer ') ? personaName : null;

  let head: string;
  let glyph = '';
  if (dim === 'persona') { glyph = '👤'; head = value == null || resolvedName == null ? 'Unknown persona' : resolvedName; }
  else if (dim === 'phase') { head = ROUND_LABEL(value); }
  // dim=turn catch-all says "No turn" (parallels "No round"; "Unbound" reads as
  // the orphan tray). Unreachable in practice — emitted tiles always have a
  // source_turn_id — but kept consistent/defensive.
  else { head = value == null ? 'No turn' : 'Turn'; }

  const solo = soloOfDim
    ? ` (only ${dim === 'persona' ? 'persona' : dim === 'phase' ? 'round' : 'turn'})`
    : '';

  return (
    <div
      style={{ width: bbox.w, height: bbox.h }}
      className="rounded-lg border border-dashed border-gray-300/70 bg-gray-50/40 dark:border-gray-600/70 dark:bg-gray-800/30 pointer-events-none"
    >
      <div className="px-2 pt-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 truncate">
        {glyph && <span aria-hidden="true">{glyph} </span>}
        {head} · {count}{solo}
      </div>
    </div>
  );
};

export default ClusterAabbNode;
